import React, { useRef, useState, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Center, Environment, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';

// --- Geometry Generators (Unchanged) ---

const createMolarShape = () => {
    const shape = new THREE.Shape();
    shape.moveTo(5 - 20, 12 - 20);
    shape.bezierCurveTo(5 - 20, 8 - 20, 8 - 20, 5 - 20, 12 - 20, 5 - 20);
    shape.lineTo(28 - 20, 5 - 20);
    shape.bezierCurveTo(32 - 20, 5 - 20, 35 - 20, 8 - 20, 35 - 20, 12 - 20);
    shape.lineTo(35 - 20, 25 - 20);
    shape.bezierCurveTo(35 - 20, 32 - 20, 30 - 20, 35 - 20, 20 - 20, 35 - 20);
    shape.bezierCurveTo(10 - 20, 35 - 20, 5 - 20, 32 - 20, 5 - 20, 25 - 20);
    shape.closePath();
    return shape;
};

const createPremolarShape = () => {
    const shape = new THREE.Shape();
    shape.absellipse(0, 0, 12, 14, 0, Math.PI * 2, false, 0);
    return shape;
};

const createCanineShape = () => {
    const shape = new THREE.Shape();
    shape.moveTo(20 - 20, 4 - 20);
    shape.lineTo(32 - 20, 15 - 20);
    shape.lineTo(30 - 20, 34 - 20);
    shape.lineTo(10 - 20, 34 - 20);
    shape.lineTo(8 - 20, 15 - 20);
    shape.closePath();
    return shape;
};

const createIncisorShape = () => {
    const shape = new THREE.Shape();
    const x = 10 - 20, y = 6 - 20, w = 20, h = 28, r = 4;
    shape.moveTo(x + r, y);
    shape.lineTo(x + w - r, y);
    shape.quadraticCurveTo(x + w, y, x + w, y + r);
    shape.lineTo(x + w, y + h - r);
    shape.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    shape.lineTo(x + r, y + h);
    shape.quadraticCurveTo(x, y + h, x, y + h - r);
    shape.lineTo(x, y + r);
    shape.quadraticCurveTo(x, y, x + r, y);
    return shape;
};

const getToothWidth = (type) => {
    switch (type) {
        case 'molar': return 35;
        case 'premolar': return 26;
        case 'canine': return 24;
        case 'incisor': default: return 22;
    }
};

// --- 3D Tooth Component ---

const Tooth3D = ({ tooth, position, rotation, isSelected, onClick, isUpper }) => {
    const meshRef = useRef();
    const [hovered, setHover] = useState(false);

    const shape = useMemo(() => {
        switch (tooth.type) {
            case 'molar': return createMolarShape();
            case 'premolar': return createPremolarShape();
            case 'canine': return createCanineShape();
            case 'incisor': default: return createIncisorShape();
        }
    }, [tooth.type]);

    const extrudeSettings = useMemo(() => ({
        depth: 8,
        bevelEnabled: true,
        bevelSegments: 2,
        steps: 2,
        bevelSize: 1,
        bevelThickness: 1
    }), []);

    useFrame((state) => {
        if (!meshRef.current) return;
        const targetScale = hovered || isSelected ? 1.15 : 1;
        meshRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.15);

        // --- Animation Correction ---
        // Upper Teeth (Top) -> Move DOWN (-6)
        // Lower Teeth (Bottom) -> Move UP (+6)
        // Splitting logic because previous unified -6 caused Lower to go Down.
        const offset = isSelected ? (isUpper ? -6 : 6) : 0;
        const targetY = position[1] + offset;

        meshRef.current.position.y = THREE.MathUtils.lerp(meshRef.current.position.y, targetY, 0.1);
    });

    return (
        <group position={[position[0], 0, position[2]]} rotation={rotation}>
            <mesh
                ref={meshRef}
                onClick={(e) => { e.stopPropagation(); onClick(tooth.num); }}
                onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
                onPointerOut={() => { setHover(false); document.body.style.cursor = 'auto'; }}
                rotation={[isUpper ? 0 : Math.PI, 0, 0]}
                position={[0, position[1], 0]}
            >
                <extrudeGeometry args={[shape, extrudeSettings]} />
                <meshStandardMaterial
                    color={isSelected ? "#3b82f6" : (hovered ? "#cbd5e1" : "#f8fafc")}
                    emissive={isSelected ? "#1d4ed8" : "#0f172a"}
                    emissiveIntensity={isSelected ? 0.4 : 0.05}
                    roughness={0.4}
                    metalness={0.2}
                />
            </mesh>
            <Text
                position={[0, isUpper ? 25 : -25, 5]}
                fontSize={5}
                color="#64748b"
                anchorX="center"
                anchorY="middle"
                rotation={[0, 0, 0]}
            >
                {tooth.num}
            </Text>
        </group>
    );
};

// --- Adaptive Jaw Arch ---

const JawArch = ({ teeth, isUpper, onToothToggle, selectedTeeth, teethType }) => {
    // Config based on Type
    const config = useMemo(() => {
        if (teethType === 'adult') {
            return {
                radius: 120,
                maxAngle: Math.PI * 1.35,
                yOffset: 40,
                baseGap: 4
            };
        } else {
            return {
                radius: 80,
                maxAngle: Math.PI * 1.25,
                yOffset: 30,
                baseGap: 2
            };
        }
    }, [teethType]);

    const processedTeeth = useMemo(() => {
        const { radius, maxAngle, baseGap } = config;

        // Proportional Spacing Logic
        const widths = teeth.map(t => getToothWidth(t.type));
        const totalToothWidth = widths.reduce((sum, w) => sum + w, 0);
        const count = teeth.length;

        const arcLength = radius * maxAngle;
        const totalGapAvailable = arcLength - totalToothWidth;
        const dynamicGap = Math.max(baseGap, totalGapAvailable / (count - 1));

        const totalContentWidth = totalToothWidth + (count - 1) * dynamicGap;

        const result = [];
        let currentPos = -totalContentWidth / 2;

        teeth.forEach((tooth, index) => {
            const width = widths[index];
            const centerOffset = currentPos + (width / 2);

            const angle = centerOffset / radius;

            const x = radius * Math.sin(angle);
            const z = radius * Math.cos(angle) - (radius * 0.5);
            const rotY = angle;

            result.push({
                ...tooth,
                position: [x, 0, z],
                rotation: [0, rotY, 0]
            });

            currentPos += width + dynamicGap;
        });

        return result;

    }, [teeth, config]);

    return (
        <group position={[0, isUpper ? config.yOffset : -config.yOffset, 0]}>
            {processedTeeth.map((tooth) => (
                <Tooth3D
                    key={tooth.num}
                    tooth={tooth}
                    position={tooth.position}
                    rotation={tooth.rotation}
                    isSelected={selectedTeeth.includes(tooth.num)}
                    onClick={onToothToggle}
                    isUpper={isUpper}
                />
            ))}
        </group>
    );
};

export const adultUpperTeeth = [
    { num: '1', label: 'UR1', type: 'molar', fullName: 'Upper right third molar (wisdom tooth)' },
    { num: '2', label: 'UR2', type: 'molar', fullName: 'Upper right second molar' },
    { num: '3', label: 'UR3', type: 'molar', fullName: 'Upper right first molar' },
    { num: '4', label: 'UR4', type: 'premolar', fullName: 'Upper right second premolar' },
    { num: '5', label: 'UR5', type: 'premolar', fullName: 'Upper right first premolar' },
    { num: '6', label: 'UR6', type: 'canine', fullName: 'Upper right canine' },
    { num: '7', label: 'UR7', type: 'incisor', fullName: 'Upper right lateral incisor' },
    { num: '8', label: 'UR8', type: 'incisor', fullName: 'Upper right central incisor' },
    { num: '9', label: 'UL1', type: 'incisor', fullName: 'Upper left central incisor' },
    { num: '10', label: 'UL2', type: 'incisor', fullName: 'Upper left lateral incisor' },
    { num: '11', label: 'UL3', type: 'canine', fullName: 'Upper left canine' },
    { num: '12', label: 'UL4', type: 'premolar', fullName: 'Upper left first premolar' },
    { num: '13', label: 'UL5', type: 'premolar', fullName: 'Upper left second premolar' },
    { num: '14', label: 'UL6', type: 'molar', fullName: 'Upper left first molar' },
    { num: '15', label: 'UL7', type: 'molar', fullName: 'Upper left second molar' },
    { num: '16', label: 'UL8', type: 'molar', fullName: 'Upper left third molar (wisdom tooth)' }
];

export const adultLowerTeeth = [
    { num: '32', label: 'LR1', type: 'molar', fullName: 'Lower right third molar (wisdom tooth)' },
    { num: '31', label: 'LR2', type: 'molar', fullName: 'Lower right second molar' },
    { num: '30', label: 'LR3', type: 'molar', fullName: 'Lower right first molar' },
    { num: '29', label: 'LR4', type: 'premolar', fullName: 'Lower right second premolar' },
    { num: '28', label: 'LR5', type: 'premolar', fullName: 'Lower right first premolar' },
    { num: '27', label: 'LR6', type: 'canine', fullName: 'Lower right canine' },
    { num: '26', label: 'LR7', type: 'incisor', fullName: 'Lower right lateral incisor' },
    { num: '25', label: 'LR8', type: 'incisor', fullName: 'Lower right central incisor' },
    { num: '24', label: 'LL1', type: 'incisor', fullName: 'Lower left central incisor' },
    { num: '23', label: 'LL2', type: 'incisor', fullName: 'Lower left lateral incisor' },
    { num: '22', label: 'LL3', type: 'canine', fullName: 'Lower left canine' },
    { num: '21', label: 'LL4', type: 'premolar', fullName: 'Lower left first premolar' },
    { num: '20', label: 'LL5', type: 'premolar', fullName: 'Lower left second premolar' },
    { num: '19', label: 'LL6', type: 'molar', fullName: 'Lower left first molar' },
    { num: '18', label: 'LL7', type: 'molar', fullName: 'Lower left second molar' },
    { num: '17', label: 'LL8', type: 'molar', fullName: 'Lower left third molar (wisdom tooth)' }
];

export const childUpperTeeth = [
    { num: 'A', label: 'URA', type: 'molar' }, { num: 'B', label: 'URB', type: 'molar' },
    { num: 'C', label: 'URC', type: 'canine' }, { num: 'D', label: 'URD', type: 'incisor' },
    { num: 'E', label: 'URE', type: 'incisor' }, { num: 'F', label: 'ULF', type: 'incisor' },
    { num: 'G', label: 'ULG', type: 'incisor' }, { num: 'H', label: 'ULH', type: 'canine' },
    { num: 'I', label: 'ULI', type: 'molar' }, { num: 'J', label: 'ULJ', type: 'molar' }
];

export const childLowerTeeth = [
    { num: 'T', label: 'LRT', type: 'molar' }, { num: 'S', label: 'LRS', type: 'molar' },
    { num: 'R', label: 'LRR', type: 'canine' }, { num: 'Q', label: 'LRQ', type: 'incisor' },
    { num: 'P', label: 'LRP', type: 'incisor' }, { num: 'O', label: 'LLO', type: 'incisor' },
    { num: 'N', label: 'LLN', type: 'incisor' }, { num: 'M', label: 'LLM', type: 'canine' },
    { num: 'L', label: 'LLL', type: 'molar' }, { num: 'K', label: 'LLK', type: 'molar' }
];

export default function ThreeDTeethChart({ selectedTeeth, onToothToggle, teethType, onTypeChange, darkMode }) {

    // --- Data (Same) ---



    const currentUpperTeeth = teethType === 'adult' ? adultUpperTeeth : childUpperTeeth;
    const currentLowerTeeth = teethType === 'adult' ? adultLowerTeeth : childLowerTeeth;

    const isTouch = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

    const backgroundStyle = darkMode
        ? 'linear-gradient(to bottom, #1e293b, #0f172a)'
        : 'linear-gradient(to bottom, #f1f5f9, #cbd5e1)';

    return (
        // Responsive Container Height using min(500px, 55vh)
        <div style={{ width: '100%', height: 'min(500px, 55vh)', position: 'relative', borderRadius: '16px', overflow: 'hidden', background: backgroundStyle, transition: 'background 0.3s ease' }}>
            <div style={{ position: 'absolute', top: 16, left: 16, zIndex: 10, display: 'flex', gap: '10px' }}>
                <div className="type-toggle">
                    <button
                        type="button"
                        className={teethType === 'adult' ? 'active' : ''}
                        onClick={() => onTypeChange('adult')}
                        style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: teethType === 'adult' ? '#2563eb' : '#fff', color: teethType === 'adult' ? '#fff' : '#334155', cursor: 'pointer', fontWeight: 600, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                    >
                        Adult
                    </button>
                    <button
                        type="button"
                        className={teethType === 'child' ? 'active' : ''}
                        onClick={() => onTypeChange('child')}
                        style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: teethType === 'child' ? '#2563eb' : '#fff', color: teethType === 'child' ? '#fff' : '#334155', cursor: 'pointer', fontWeight: 600, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                    >
                        Child
                    </button>
                </div>
                <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.8)', borderRadius: '8px', fontSize: '0.85rem', color: '#475569', display: 'flex', alignItems: 'center' }}>
                    {isTouch ? '👆 Touch to Interact' : '🖱️ Scroll/Drag to Interact'}
                </div>
            </div>

            <Canvas camera={{ position: [0, 90, 220], fov: 45 }} shadows dpr={[1, 2]}>
                <ambientLight intensity={0.9} />
                <spotLight position={[50, 80, 50]} angle={0.25} penumbra={1} intensity={0.8} castShadow />
                <pointLight position={[-30, 20, -30]} intensity={0.6} color="#e0f2fe" />

                <OrbitControls
                    enablePan={true}
                    enableZoom={true}
                    minDistance={40}
                    maxDistance={400}
                    minPolarAngle={0}
                    maxPolarAngle={Math.PI / 1.5}
                    makeDefault
                />

                <Center>
                    <group rotation={[0, 0, 0]} position={[0, 0, 0]}>
                        <JawArch
                            teeth={currentUpperTeeth}
                            isUpper={true}
                            onToothToggle={onToothToggle}
                            selectedTeeth={selectedTeeth}
                            teethType={teethType}
                        />
                        <JawArch
                            teeth={currentLowerTeeth}
                            isUpper={false}
                            onToothToggle={onToothToggle}
                            selectedTeeth={selectedTeeth}
                            teethType={teethType}
                        />
                    </group>
                </Center>

                <Environment preset="city" />
                <ContactShadows position={[0, -50, 0]} opacity={0.3} scale={150} blur={2.5} far={50} />
            </Canvas>
        </div>
    );
}
